import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Factory, Package, FileText, FolderOpen, Settings,
  ShieldCheck, LogOut, ChevronDown, CreditCard,
} from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/app/plants", label: "Plants", icon: Factory, testid: "nav-plants" },
  { to: "/app/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/app/epds", label: "EPDs", icon: FileText, testid: "nav-epds" },
  { to: "/app/evidence", label: "Data & Evidence", icon: FolderOpen, testid: "nav-evidence" },
  { to: "/app/billing", label: "Subscription & Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSuper = user?.role === "super_admin";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-[#0f172a] text-slate-100 flex flex-col fixed h-screen">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2.5">
          <BrandMark size={34} />
          <div>
            <div className="font-display font-semibold text-[15px] leading-none">EPD Nigeria</div>
            <div className="text-[10px] text-slate-400 mt-1">Enterprise EPD infrastructure</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => `epn-sidebar-link ${isActive ? "active" : ""}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          {isSuper && (
            <NavLink to="/app/admin" data-testid="nav-admin"
              className={({ isActive }) => `epn-sidebar-link ${isActive ? "active" : ""}`}>
              <ShieldCheck className="h-4 w-4" /> Administration
            </NavLink>
          )}
        </nav>
        <div className="px-3 py-3 border-t border-white/10 text-[11px] text-slate-500">
          COLLECT · CALCULATE · CHECK · GENERATE · PUBLISH
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-60 flex flex-col min-h-screen">
        <header className="h-14 border-b bg-white flex items-center justify-between px-6 sticky top-0 z-20">
          <div className="text-sm text-muted-foreground">
            {user?.organization?.legal_name || (isSuper ? "Platform Administration" : "")}
            {user?.organization?.subscription_status && (
              <span className="ml-2 text-xs rounded border px-1.5 py-0.5 bg-slate-50">
                {user.organization.subscription_status}
              </span>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 text-sm hover:bg-slate-50 rounded-md px-2 py-1.5">
                <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-semibold">
                  {(user?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="text-left leading-tight">
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground">{user?.role_label}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-6 md:p-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
